import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SchemaOrg from '@/components/base/SchemaOrg';
import { articles } from '@/mocks/categories';
import { articleContents } from '@/mocks/articleContents';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://pandabuy.study';

const categoryLinks = [
  { label: 'Shoes', href: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { label: 'Hoodies', href: 'https://kaspreadsheet.com/search-912/name/Shoes.html' },
  { label: 'T-Shirts', href: 'https://kaspreadsheet.com/search-913/name/Shoes.html' },
  { label: 'Jackets', href: 'https://kaspreadsheet.com/search-914/name/Shoes.html' },
  { label: 'Bags', href: 'https://kaspreadsheet.com/search-921/name/Shoes.html' },
];

function buildArticleSchema(article: typeof articles[0]) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.description,
    image: article.image,
    datePublished: '2026-06-30',
    dateModified: '2026-07-01',
    author: {
      '@type': 'Organization',
      name: 'PandabuySpreadsheet Hub',
      url: siteUrl,
    },
    publisher: {
      '@type': 'Organization',
      name: 'PandabuySpreadsheet Hub',
      logo: {
        '@type': 'ImageObject',
        url: 'https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2ce33a4b04813007a8211abaa8144b8a.jpeg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `${siteUrl}/article/${article.slug}`,
    },
  };
}

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'instant' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="min-h-screen bg-background-50">
        <Navbar />
        <main className="pt-20 pb-16">
          <div className="w-full px-4 max-w-4xl mx-auto text-center py-20">
            <h1 className="text-2xl font-heading text-foreground-950 mb-4">Article Not Found</h1>
            <p className="text-foreground-600 mb-6">The article you are looking for does not exist or may have been moved.</p>
            <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 text-sm font-semibold font-label bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer">
              <i className="ri-home-line"></i> Back to PandabuySheet Hub
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleContent = articleContents[article.slug] || '';
  const articleSchema = buildArticleSchema(article);

  return (
    <div className="min-h-screen bg-background-50">
      <SchemaOrg data={articleSchema} />
      <Navbar />

      <main>
        <article>
          <header className="relative w-full h-[280px] md:h-[400px] overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="absolute inset-0 w-full h-full object-cover object-top"
              loading="eager"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/25 to-black/50"></div>
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="w-full max-w-4xl px-4 text-center">
                <span className="inline-flex items-center gap-1.5 text-xs font-medium text-accent-200 bg-accent-500/20 backdrop-blur-sm rounded-full px-4 py-1.5 mb-4 font-label">
                  <i className="ri-article-line"></i> {article.tag}
                </span>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading text-white leading-tight">
                  {article.title}
                </h1>
                <p className="text-sm md:text-base text-white/75 mt-4 max-w-2xl mx-auto">
                  {article.subtitle}
                </p>
              </div>
            </div>
          </header>

          <div className="w-full px-4 md:px-10 max-w-4xl mx-auto py-10 md:py-14">
            <div className="flex flex-wrap items-center gap-3 text-xs text-foreground-500 mb-8 font-label">
              <span className="flex items-center gap-1.5">
                <i className="ri-calendar-line"></i> Updated June 2026
              </span>
              <span className="text-foreground-300">|</span>
              <span className="flex items-center gap-1.5">
                <i className="ri-time-line"></i> {article.readTime}
              </span>
              <span className="text-foreground-300">|</span>
              <Link to="/" className="flex items-center gap-1.5 text-primary-600 hover:text-primary-700 transition-colors">
                <i className="ri-home-line"></i> Back to PandabuySheet Hub
              </Link>
            </div>

            <div
              className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: articleContent }}
            />

            <div className="mt-10 p-6 bg-background-100 border border-background-200/70 rounded-2xl">
              <h4 className="text-base font-semibold font-label text-foreground-950 mb-3">
                Ready to Start Exploring?
              </h4>
              <p className="text-sm text-foreground-600 mb-5">
                Browse our complete pandabuy spreadsheet hub with 14 curated categories, updated weekly for the community.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <Link
                  to="/"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold font-label bg-primary-500 text-background-50 rounded-lg hover:bg-primary-600 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-home-line"></i>
                  Back to PandabuySheet Hub
                </Link>
                <a
                  href="https://kaspreadsheet.com"
                  target="_blank"
                  rel="noopener nofollow"
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold font-label bg-foreground-950 text-background-50 rounded-lg hover:bg-foreground-800 transition-all duration-200 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-external-link-line"></i>
                  Open Pandabuy Sheet
                </a>
              </div>
            </div>

            <div className="mt-10 pt-8 border-t border-background-200/70">
              <h4 className="text-sm font-semibold font-label text-foreground-500 mb-3 uppercase tracking-wide">
                Internal Links
              </h4>
              <div className="flex flex-wrap gap-2">
                <Link to="/" className="text-sm text-primary-600 hover:text-primary-700 transition-colors">
                  PandabuySheet Hub Homepage
                </Link>
                {categoryLinks.map((cat) => (
                  <span key={cat.label} className="flex items-center gap-2">
                    <span className="text-foreground-300">·</span>
                    <a
                      href={cat.href}
                      target="_blank"
                      rel="noopener nofollow"
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors"
                    >
                      {cat.label}
                    </a>
                  </span>
                ))}
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-background-200/70">
              <h4 className="text-sm font-semibold font-label text-foreground-500 mb-3 uppercase tracking-wide">
                Related PandabuySheet Guides
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {articles
                  .filter((a) => a.slug !== article.slug)
                  .slice(0, 6)
                  .map((related) => (
                    <Link
                      key={related.slug}
                      to={`/article/${related.slug}`}
                      className="text-sm text-foreground-600 hover:text-primary-600 transition-colors duration-150 py-1.5"
                    >
                      <i className="ri-arrow-right-line text-xs mr-1.5 text-primary-400"></i>
                      {related.title}
                    </Link>
                  ))}
              </div>
            </div>
          </div>
        </article>
      </main>

      <Footer />
    </div>
  );
}